import { Link } from "@tanstack/react-router";
import {
  AgentsIcon,
  AlarmClockIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
  FileIcon,
  MessagesSquareIcon,
  ShieldCheckIcon,
  WrenchIcon,
  ZapIcon,
} from "lucide-react";
import { useMemo } from "react";

import { useI18n } from "~/i18n";
import { usePrimaryEnvironmentId } from "../../state/environments";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetPanel,
  SheetPopup,
  SheetTitle,
} from "../ui/sheet";
import { MorrowPersonAvatar, personRoleLine } from "../workspace/MorrowPersonCard";
import { RunStepsPanel } from "../workflows/RunStepsPanel";
import type { ActivityEvent } from "./activitiesTypes";
import { ActivityLifecycleStepper } from "./ActivityLifecycleStepper";
import {
  activityDetailLinks,
  type ActivityDetailLinkId,
  type ActivityDetailRouteLink,
} from "./activityDetailLinks";
import {
  activityKindLabelKey,
  activityKindIcon,
  activityLifecycleSteps,
  activityStatusBadgeClass,
  activityStatusLabelKey,
  agentDisplayLabel,
  formatActivityDateTime,
  toolkitLogoUrl,
} from "./activityPresentation";

export function ActivityDetailSheet({
  event,
  mockMode,
  open,
  onOpenChange,
}: {
  readonly event: ActivityEvent | null;
  readonly mockMode: boolean;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const { t, locale } = useI18n();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const KindIcon = event ? activityKindIcon(event.kind) : null;
  const lifecycleSteps = event ? activityLifecycleSteps(event) : [];
  const links = useMemo(
    () => (event ? activityDetailLinks(event, primaryEnvironmentId) : []),
    [event, primaryEnvironmentId],
  );
  const showFileMeta = Boolean(event?.refs.fileLabel && !event?.refs.vaultItemId);

  const closeSheet = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetPopup side="right" className="max-w-md">
        {event ? (
          <>
            <SheetHeader>
              <SheetTitle className="text-base">{event.title}</SheetTitle>
              <SheetDescription className="text-sm">{event.summary}</SheetDescription>
            </SheetHeader>
            <SheetPanel className="flex flex-col gap-4 px-6 pb-6">
              <div className="flex items-center gap-3">
                <MorrowPersonAvatar name={event.actor.name} avatarSeed={event.actor.avatarSeed} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{event.actor.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {personRoleLine(event.actor.title, event.actor.role)}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant="outline"
                  className={`gap-1 ${activityStatusBadgeClass(event.status)}`}
                >
                  {t(activityStatusLabelKey(event.status))}
                </Badge>
                <Badge variant="outline" className="gap-1">
                  {KindIcon ? <KindIcon className="size-3 shrink-0" aria-hidden /> : null}
                  {t(activityKindLabelKey(event.kind))}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {formatActivityDateTime(event.occurredAt, locale)}
                </span>
              </div>

              {event.kind === "comms" && event.refs.toAgentLabel ? (
                <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-sm">
                  <span className="min-w-0 truncate font-medium text-foreground">
                    {event.actor.name}
                  </span>
                  <ArrowRightIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="min-w-0 truncate font-medium text-foreground">
                    {event.refs.toAgentLabel}
                  </span>
                </div>
              ) : null}

              {event.dueAt && (event.status === "needs_approval" || event.status === "pending") ? (
                <DetailRow
                  icon={<AlarmClockIcon className="size-4" aria-hidden />}
                  label="Due"
                  value={formatActivityDateTime(event.dueAt, locale)}
                />
              ) : null}

              <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-3">
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80">
                  {t("activities.detail.progress")}
                </p>
                <ActivityLifecycleStepper steps={lifecycleSteps} />
              </div>

              {agentDisplayLabel(event) ? (
                <DetailRow
                  icon={<AgentsIcon className="size-4" aria-hidden />}
                  label={t("activities.detail.agent")}
                  value={agentDisplayLabel(event)!}
                />
              ) : null}

              {event.refs.toolName ? (
                <DetailRow
                  icon={<WrenchIcon className="size-4" />}
                  label={t("activities.detail.tool")}
                  value={
                    <span className="inline-flex items-center gap-2">
                      {toolkitLogoUrl(event.refs.toolkit) ? (
                        <img
                          src={toolkitLogoUrl(event.refs.toolkit)!}
                          alt=""
                          className="size-4 rounded"
                        />
                      ) : null}
                      {event.refs.toolName}
                    </span>
                  }
                />
              ) : null}

              {showFileMeta ? (
                <DetailRow
                  icon={<FileIcon className="size-4" />}
                  label={t("activities.detail.file")}
                  value={event.refs.fileLabel}
                />
              ) : null}

              {event.refs.workflowName && !event.refs.workflowId ? (
                <DetailRow
                  icon={<ZapIcon className="size-4" aria-hidden />}
                  label={t("activities.detail.workflow")}
                  value={event.refs.workflowName}
                />
              ) : null}

              {event.kind === "workflow_run" && event.refs.runId ? (
                <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-3">
                  <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80">
                    {t("activities.detail.runSteps")}
                  </p>
                  <RunStepsPanel runId={event.refs.runId} graph={null} runStatus={event.status} />
                </div>
              ) : null}

              {links.length > 0 ? (
                <div className="flex flex-col gap-2 border-t border-border pt-4">
                  <p className="text-xs font-medium text-muted-foreground">
                    {t("activities.detail.links")}
                  </p>
                  {mockMode ? (
                    <p className="text-xs text-muted-foreground/80">
                      {t("activities.mock.linksHint")}
                    </p>
                  ) : null}
                  <div className="flex flex-col gap-2">
                    {links.map((link) => (
                      <ActivityDetailLinkTile
                        key={link.id}
                        link={link}
                        mockMode={mockMode}
                        onNavigate={closeSheet}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </SheetPanel>
          </>
        ) : null}
      </SheetPopup>
    </Sheet>
  );
}

function ActivityDetailLinkTile({
  link,
  mockMode,
  onNavigate,
}: {
  readonly link: ActivityDetailRouteLink;
  readonly mockMode: boolean;
  readonly onNavigate: () => void;
}) {
  const { t } = useI18n();
  const Icon = linkIcon(link.id);
  const label = t(link.labelKey);
  const subtitle = link.subtitle?.trim();

  if (mockMode) {
    return (
      <div
        className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/15 px-3 py-2.5 opacity-60"
        aria-disabled
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border/60 bg-background">
          <Icon className="size-4 text-muted-foreground" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">{label}</p>
          {subtitle ? <p className="truncate text-xs text-muted-foreground">{subtitle}</p> : null}
        </div>
      </div>
    );
  }

  return (
    <Button
      variant="ghost"
      className="h-auto w-full justify-start gap-3 rounded-xl border-border/70 bg-card px-3 py-2.5 text-left font-normal sm:h-auto [:hover,[data-pressed]]:bg-muted/35"
      render={activityLinkRender(link)}
      onClick={onNavigate}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/30">
        <Icon className="size-4 text-muted-foreground" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          {label}
          <ArrowUpRightIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        </span>
        {subtitle ? (
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">{subtitle}</span>
        ) : null}
      </span>
    </Button>
  );
}

function linkIcon(id: ActivityDetailLinkId) {
  switch (id) {
    case "thread":
      return MessagesSquareIcon;
    case "agent":
      return AgentsIcon;
    case "workflow":
      return ZapIcon;
    case "allowance":
      return ShieldCheckIcon;
    case "file":
      return FileIcon;
    default:
      return ArrowUpRightIcon;
  }
}

function activityLinkRender(link: ActivityDetailRouteLink): React.ReactElement {
  switch (link.id) {
    case "thread": {
      const environmentId = link.params?.environmentId;
      const threadId = link.params?.threadId;
      if (!environmentId || !threadId) {
        return <Link to="/activities" />;
      }
      return <Link to="/$environmentId/$threadId" params={{ environmentId, threadId }} />;
    }
    case "agent": {
      const agentId = link.params?.agentId;
      if (!agentId) {
        return <Link to="/activities" />;
      }
      return <Link to="/agents/$agentId" params={{ agentId }} />;
    }
    case "workflow": {
      const id = link.params?.id;
      if (!id) {
        return <Link to="/activities" />;
      }
      return <Link to="/workflows/$id" params={{ id }} />;
    }
    case "allowance":
      return <Link to="/allowance" />;
    case "file": {
      const item = link.search?.item;
      if (!item) {
        return <Link to="/vault" />;
      }
      return <Link to="/vault" search={{ item }} />;
    }
    default:
      return <Link to="/activities" />;
  }
}

function DetailRow({
  icon,
  label,
  value,
}: {
  readonly icon?: React.ReactNode;
  readonly label: string;
  readonly value: React.ReactNode;
}) {
  return (
    <div className="flex gap-2 text-sm">
      {icon ? <span className="mt-0.5 text-muted-foreground">{icon}</span> : null}
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80">
          {label}
        </p>
        <div className="mt-0.5 text-foreground">{value}</div>
      </div>
    </div>
  );
}
